// Side-effect imports: register each provider with the registry at
// module load. Every browser conversion provider MUST be imported
// here (or transitively) before the first `fireConversion` call —
// an unimported provider is an unregistered provider.
import "@/features/tracking/providers/campaignswell/dispatchCampaignswellConversion";
import "@/features/tracking/providers/meta/dispatchMetaConversion";

import { dispatchConversionToAllProviders } from "@/features/tracking/registry/conversionProviderRegistry";
import { type FireConversionParams } from "@/features/tracking/registry/fireConversion.types";

/**
 * Fire a conversion event across every registered browser tracking
 * provider.
 *
 * Contract:
 * - Never throws; always synchronous from the caller's point of view.
 *   A misbehaving provider cannot break the call site (e.g. signup
 *   success flow) — the registry wraps every provider dispatch in
 *   try/catch.
 * - Per-provider readiness is enforced by the registry: conversions
 *   fired before a provider's pixel has initialized are queued and
 *   flushed when the provider calls
 *   `markConversionProviderReady(id)`. This closes the race where
 *   `fireConversion` can run before `useMetaPixel`'s effect has
 *   installed `window.fbq` and issued `INIT`.
 * - Dedup is the caller's responsibility via a stable `eventId`. The
 *   browser pixel and the backend CAPI producer MUST agree on this
 *   id or Meta will double-count.
 *
 * Adding a provider: create `dispatchXxxConversion`, register it via
 * `registerConversionProvider({ id, dispatch })`, wire the hook that
 * owns the pixel lifecycle to call `markConversionProviderReady` on
 * init, and add a side-effect import here. No call site changes.
 */
export default function fireConversion(params: FireConversionParams): void {
  dispatchConversionToAllProviders(params);
}
